import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Check,
  RefreshCw,
  Loader2,
  ShieldCheck,
  Gift,
  Copy,
  Mail,
  MessageCircle,
  Receipt,
  X,
} from "lucide-react";
import QRCode from "react-qr-code";

import { DOC_TYPES, ISO3_TO_ISTAT } from "./constants";
import GeoSearchSelect from "./GeoSearchSelect";
import ScanModePicker from "./ScanModePicker";
import NfcDropZone from "./NfcDropZone";
import CaptureStep from "./CaptureStep";

// -----------------------------------------------------------------------------
// Scan Dialog — camera capture / file upload → OCR → editable form → save
// Extracted from ReceptionTab.jsx (Iteration 62 — modularisation).
// The `property` prop is required so we can respect the host's
// `require_arrival_time` toggle without leaking Reception state.
// -----------------------------------------------------------------------------
export default function ScanDialog({ booking, onClose, onDone, onArrivalTimeSaved, startInManual = false, property = null }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const open = !!booking;
  const [step, setStep] = useState(startInManual ? "review" : "modepick"); // modepick | capture | processing | review | nfc
  const [scanMode, setScanMode] = useState(startInManual ? "manual" : null); // mrz | ocr | manual | nfc | null
  // NFC queue lifted here so it survives step transitions (review→nfc→review
  // during batch group registration). See NfcDropZone controlled prop.
  const [nfcQueue, setNfcQueue] = useState([]);
  const [imageB64, setImageB64] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [mrzParsed, setMrzParsed] = useState(null);
  const [mrzComparison, setMrzComparison] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedGuests, setSavedGuests] = useState([]); // ospiti registrati in questa sessione
  const [welcome, setWelcome] = useState(null); // { url, emailSent } shown after first save
  const [hunt, setHunt] = useState(null); // { available, hunt, deep_link, whatsapp_url }
  // Payment status editor rendered at the top of the review step so the host
  // can flip a walk-in from "Da incassare" to "Pagato" without leaving the
  // scan flow. Mirrors the same PATCH used by Prenotazioni + OTA modal.
  const [paymentStatus, setPaymentStatus] = useState(booking?.payment_status || "pending");
  const [savingPs, setSavingPs] = useState(false);
  useEffect(() => { setPaymentStatus(booking?.payment_status || "pending"); }, [booking?.id, booking?.payment_status]);
  const updatePaymentStatus = async (v) => {
    if (!booking?.id || v === paymentStatus) return;
    // Guard against blank/whitespace ids — surfaces early instead of
    // hitting the "Booking not found" server response.
    const bid = String(booking.id || "").trim();
    if (!bid) {
      toast.error(IT ? "ID prenotazione mancante" : "Missing booking id");
      return;
    }
    const prev = paymentStatus;
    setPaymentStatus(v);
    setSavingPs(true);
    try {
      await api.patch(`/bookings/${encodeURIComponent(bid)}/payment-status`, { payment_status: v });
      toast.success(IT ? "Stato pagamento aggiornato" : "Payment status updated");
    } catch (e) {
      setPaymentStatus(prev);
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSavingPs(false); }
  };
  // Departed toggle — reflects reception_status='departed' / checked_out_at.
  // Editable directly from the guest sheet so the host can flip it without
  // going back to the reception table.
  const [departed, setDeparted] = useState(
    booking?.reception_status === "departed" || !!booking?.checked_out_at
  );
  const [savingDep, setSavingDep] = useState(false);
  // Arrived toggle — mirror of departed for symmetry: host can flip check-in
  // status straight from the guest sheet.
  const [arrived, setArrived] = useState(
    booking?.reception_status === "arrived" || !!booking?.checked_in_at
  );
  const [savingArr, setSavingArr] = useState(false);
  useEffect(() => {
    setDeparted(booking?.reception_status === "departed" || !!booking?.checked_out_at);
    setArrived(booking?.reception_status === "arrived" || !!booking?.checked_in_at);
  }, [booking?.id, booking?.reception_status, booking?.checked_out_at, booking?.checked_in_at]);
  const toggleDeparted = async (next) => {
    if (!booking?.id) return;
    const prev = departed;
    setDeparted(next);
    setSavingDep(true);
    try {
      const isOta = !!booking.is_ota;
      const bid = isOta && !String(booking.id).startsWith("ota-") ? `ota-${booking.id}` : booking.id;
      await api.post("/reception/mark-departed", { booking_id: bid, is_ota: isOta, departed: next });
      toast.success(next
        ? (IT ? "Ospite segnato come partito" : "Guest marked as departed")
        : (IT ? "Ripristinato come 'in casa'" : "Reverted to 'in-house'"));
    } catch (e) {
      setDeparted(prev);
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSavingDep(false); }
  };
  const toggleArrived = async (next) => {
    if (!booking?.id) return;
    const prev = arrived;
    setArrived(next);
    setSavingArr(true);
    try {
      const isOta = !!booking.is_ota;
      const bid = isOta && !String(booking.id).startsWith("ota-") ? `ota-${booking.id}` : booking.id;
      await api.post("/reception/mark-arrived", { booking_id: bid, is_ota: isOta, arrived: next });
      toast.success(next
        ? (IT ? "Ospite segnato come arrivato" : "Guest marked as arrived")
        : (IT ? "Ripristinato come 'in attesa'" : "Reverted to 'pending'"));
    } catch (e) {
      setArrived(prev);
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSavingArr(false); }
  };
  // Editable arrival_time in the guest sheet — same PATCH the Prenotazioni
  // and Reception rows use. Empty string clears the override.
  const [arrivalTime, setArrivalTime] = useState(booking?.arrival_time || "");
  const [savingAt, setSavingAt] = useState(false);
  useEffect(() => { setArrivalTime(booking?.arrival_time || ""); }, [booking?.id, booking?.arrival_time]);
  const saveArrivalTime = async (val) => {
    if (!booking?.id) return;
    const trimmed = (val || "").trim();
    if (trimmed && !/^\d{2}:\d{2}$/.test(trimmed)) {
      toast.error(IT ? "Formato ora non valido (HH:MM)" : "Invalid time (HH:MM)");
      return;
    }
    setSavingAt(true);
    try {
      const isOta = !!booking.is_ota;
      const bid = isOta && !String(booking.id).startsWith("ota-") ? `ota-${booking.id}` : booking.id;
      await api.patch(`/bookings/${encodeURIComponent(bid)}/arrival-time`, { arrival_time: trimmed });
      toast.success(trimmed
        ? (IT ? `Ora arrivo aggiornata a ${trimmed}` : `Arrival time set to ${trimmed}`)
        : (IT ? "Ora arrivo azzerata" : "Arrival time cleared"));
      // Notify parent so the Reception table cell refreshes without a full
      // reload — otherwise the ArrivalTimeCell keeps the old value until
      // the next full arrivals fetch.
      onArrivalTimeSaved?.(booking.id, trimmed || null);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSavingAt(false); }
  };
  // Emetti ricevuta — on-demand issuance from the scan flow. If not paid we
  // promote to paid (backend auto-fills paid_at) then generate. Only enabled
  // for direct bookings (OTA rows are read-only from an invoicing standpoint).
  const [issuing, setIssuing] = useState(false);
  const isDirect = booking?.id && !booking?.is_ota
    && !String(booking.id).startsWith("ota-")
    && !String(booking.id).startsWith("cb-");
  const emettiRicevuta = async () => {
    if (!isDirect) {
      toast.error(IT ? "Emissione disponibile solo per prenotazioni dirette" : "Only direct bookings can be issued");
      return;
    }
    setIssuing(true);
    try {
      const r = await api.post(`/invoices/generate/${booking.id}`);
      const { invoice_id, number, pdf_path } = r.data;
      setPaymentStatus("paid");
      toast.success(IT ? `Ricevuta ${number} emessa` : `Receipt ${number} issued`, {
        action: {
          label: IT ? "Scarica PDF" : "Download PDF",
          onClick: async () => {
            try {
              const pdf = await api.get(`/invoices/${invoice_id}.pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([pdf.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url; a.download = `ricevuta-${number.replace("/", "-")}.pdf`;
              document.body.appendChild(a); a.click();
              setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
            } catch (err) { toast.error(errMsg(err)); }
          },
        },
      });
    } catch (e) {
      toast.error(errMsg(e, IT ? "Emissione fallita" : "Issuance failed"));
    } finally { setIssuing(false); }
  };

  // Default "Permanenza" (nights) — derived from booking dates, host can override
  // in the review form. Falls back to 1 only if dates are missing.
  const defaultNights = useMemo(() => {
    const n = Number(booking?.nights);
    if (Number.isFinite(n) && n >= 1) return n;
    try {
      const ci = booking?.check_in;
      const co = booking?.check_out;
      if (ci && co) {
        const d1 = new Date(ci.slice(0, 10));
        const d2 = new Date(co.slice(0, 10));
        const diff = Math.round((d2 - d1) / 86400000);
        if (diff >= 1) return diff;
      }
    } catch { /* noop */ }
    return 1;
  }, [booking?.nights, booking?.check_in, booking?.check_out]);

  // Session-wide "Permanenza" — applies to the whole booking, not per-guest.
  // Auto-syncs to the computed default when the booking (dates) change.
  const [nightsOverride, setNightsOverride] = useState(defaultNights);
  useEffect(() => { setNightsOverride(defaultNights); }, [defaultNights]);

  // Fetch treasure-hunt welcome payload the first time we get a saved guest.
  // Keeps the ScanDialog light on empty flows and avoids a 404-noise fetch
  // for properties without a hunt configured.
  useEffect(() => {
    if (!booking?.id || savedGuests.length === 0 || hunt) return;
    api.get(`/reception/bookings/${booking.id}/treasure-hunt`)
      .then((r) => setHunt(r.data))
      .catch(() => setHunt({ available: false }));
  }, [booking?.id, savedGuests.length, hunt]);

  useEffect(() => {
    if (!open) {
      setStep(startInManual ? "review" : "modepick");
      setScanMode(startInManual ? "manual" : null);
      setImageB64(null); setExtracted(null); setForm(null);
      setMrzParsed(null); setMrzComparison([]); setSavedGuests([]);
      setWelcome(null); setHunt(null);
      return;
    }
    // On open — rehydrate previously-registered guests for this booking so
    // the "Ospiti registrati in questa sessione" tracker survives dialog
    // close/reopen and the host doesn't see an empty scheda after doing the
    // scan. Uses the new booking_id filter on /guests. For OTA rows we
    // send the "ota-<uid>" prefixed id so the query matches how
    // /reception/checkin persists the booking_id.
    if (booking?.id) {
      const bid = booking.is_ota && !booking.id.startsWith("ota-") ? `ota-${booking.id}` : booking.id;
      api.get(`/guests?booking_id=${encodeURIComponent(bid)}`)
        .then((r) => {
          const prev = (r.data || []).map((g) => ({
            id: g.id,
            last_name: (g.last_name || "").toUpperCase(),
            first_name: (g.first_name || "").toUpperCase(),
            guest_type: g.guest_type || "16",
            doc_type: g.doc_type || "IDELE",
            doc_number: (g.doc_number || "").toUpperCase(),
          }));
          if (prev.length) setSavedGuests(prev);
        })
        .catch(() => { /* silent — empty tracker is fine on error */ });
    }
    if (startInManual && !form) {
      // Manual entry — skip OCR entirely, land straight on the empty review form
      // pre-filled with sensible defaults so the host just fills in the fields.
      setStep("review");
      const [firstFromBooking, ...restBooking] = (booking?.guest_name || "").split(" ");
      setForm({
        guest_type: savedGuests.length === 0 ? "17" : "19",
        last_name: (restBooking.join(" ") || "").toUpperCase(),
        first_name: (firstFromBooking || "").toUpperCase(),
        gender: "", date_of_birth: "",
        doc_type: "IDELE", doc_number: "", codice_fiscale: "", mrz: "",
        citizenship_code: "100000100",
        country_of_birth_code: "100000100",
        comune_of_birth_code: "",
        province_of_birth: "",
        birth_in_italy: true,
        issuing_country_iso3: "", expiry_date: "", notes: "",
        arrival_time: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startInManual, booking?.id]);

  // First guest → "16 Singolo" or "17 Capofamiglia" (if the host expects multiple);
  // subsequent → "19 Familiare" (defaults conservative, host can override).
  const nextGuestType = () => (savedGuests.length === 0 ? "17" : "19");

  const runOcr = async (b64) => {
    // In MRZ mode the CaptureStep auto-polls the camera; abort quietly when
    // the current frame doesn't contain a decodable MRZ so the loop keeps
    // scanning without flipping the whole dialog to the "processing" state.
    if (scanMode === "mrz" && step !== "processing") {
      try {
        const r = await api.post("/reception/scan-document", {
          image_base64: b64, parse_mode: "mrz",
        });
        const x = r.data.extracted || {};
        const gotMrz = r.data.mrz_parsed || (x.mrz && (x.doc_number || x.last_name));
        if (!gotMrz) return; // keep the auto-scan loop alive
        // MRZ decoded — commit to the review flow using the same path below.
        setImageB64(b64);
        setStep("review");
        setExtracted(x);
        setConfidence(x.confidence || "high");
        setMrzParsed(r.data.mrz_parsed || null);
        setMrzComparison(r.data.mrz_comparison || []);
        setForm({
          guest_type: nextGuestType(),
          last_name: x.last_name || "", first_name: x.first_name || "",
          gender: x.gender || "", date_of_birth: x.date_of_birth || "",
          doc_type: x.doc_type || "PASOR", doc_number: x.doc_number || "",
          codice_fiscale: x.codice_fiscale || "", mrz: x.mrz || "",
          citizenship_code: ISO3_TO_ISTAT[x.citizenship_iso3] || "100000100",
          country_of_birth_code: ISO3_TO_ISTAT[x.country_of_birth_iso3] || "100000100",
          comune_of_birth_code: "", province_of_birth: "",
          birth_in_italy: !x.country_of_birth_iso3 || x.country_of_birth_iso3 === "ITA",
          issuing_country_iso3: x.issuing_country_iso3 || "",
          expiry_date: x.expiry_date || "", notes: "",
          arrival_time: "",
        });
        // Fire-and-forget: pre-select the comune of birth if the MRZ carried one.
        autofillComune(x);
        return;
      } catch { return; }
    }
    setStep("processing");
    setImageB64(b64);
    try {
      const r = await api.post("/reception/scan-document", {
        image_base64: b64,
        parse_mode: scanMode || "auto",  // "mrz" | "ocr" hint to the backend
      });
      const x = r.data.extracted || {};
      setExtracted(x);
      setConfidence(x.confidence || null);
      setMrzParsed(r.data.mrz_parsed || null);
      setMrzComparison(r.data.mrz_comparison || []);
      setForm({
        guest_type: nextGuestType(),
        last_name: x.last_name || "",
        first_name: x.first_name || "",
        gender: x.gender || "",
        date_of_birth: x.date_of_birth || "",
        doc_type: x.doc_type || "IDELE",
        doc_number: x.doc_number || "",
        codice_fiscale: x.codice_fiscale || "",
        mrz: x.mrz || "",
        citizenship_code: ISO3_TO_ISTAT[x.citizenship_iso3] || "100000100",
        country_of_birth_code: ISO3_TO_ISTAT[x.country_of_birth_iso3] || "100000100",
        comune_of_birth_code: "",
        province_of_birth: "",
        birth_in_italy: !x.country_of_birth_iso3 || x.country_of_birth_iso3 === "ITA",
        issuing_country_iso3: x.issuing_country_iso3 || "",
        expiry_date: x.expiry_date || "",
        notes: "",
        arrival_time: "",
      });
      // Fire-and-forget: pre-select the comune of birth if OCR extracted one.
      autofillComune(x);
      setStep("review");
    } catch (e) {
      toast.error(errMsg(e, IT ? "OCR fallito" : "OCR failed"));
      setStep("capture");
    }
  };

  // Legacy hook — kept for parity with the pre-modularisation file. The
  // helper was never defined in the original ReceptionTab either (calls
  // wrapped in try/catch), so we mirror that no-op behaviour here to avoid
  // introducing a runtime ReferenceError. If a future iteration wants to
  // resolve the extracted city string into an ISTAT comune code, this is
  // the single place to wire it up.
  const autofillComune = (_x) => { /* intentional no-op */ };

  const save = async () => {
    if (!form) return;
    if (!form.last_name || !form.first_name) {
      toast.error(IT ? "Nome e cognome sono obbligatori" : "First and last name required");
      return;
    }
    if (!form.doc_number) {
      toast.error(IT ? "Numero documento obbligatorio" : "Document number required");
      return;
    }
    if (!form.gender) {
      toast.error(IT ? "Seleziona il sesso" : "Select gender");
      return;
    }
    if (!form.date_of_birth) {
      toast.error(IT ? "Data di nascita obbligatoria" : "Date of birth required");
      return;
    }
    if (property?.require_arrival_time && !(form.arrival_time || "").trim()) {
      toast.error(IT ? "Orario di arrivo obbligatorio per questa struttura" : "Arrival time is required for this property");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/reception/checkin", {
        booking_id: booking.id,
        guest_type: form.guest_type || "16",
        nights: Math.max(1, Math.min(30, Number(nightsOverride) || defaultNights)),
        arrival_time: (form.arrival_time || "").trim() || null,
        last_name: form.last_name,
        first_name: form.first_name,
        gender: form.gender,
        date_of_birth: form.date_of_birth,
        country_of_birth_code: form.country_of_birth_code || "100000100",
        citizenship_code: form.citizenship_code || "100000100",
        comune_of_birth_code: form.birth_in_italy ? (form.comune_of_birth_code || null) : null,
        province_of_birth: form.birth_in_italy ? (form.province_of_birth || null) : null,
        doc_type: form.doc_type,
        doc_number: form.doc_number,
        doc_place_code: ISO3_TO_ISTAT[form.issuing_country_iso3] || form.citizenship_code || "100000100",
        codice_fiscale: form.codice_fiscale || null,
        mrz: form.mrz || null,
        notes: form.notes || null,
      });
      // Capture welcome invite (only present on first registration of this booking, if guest has email + no account)
      if (r.data?.welcome_url && !welcome) {
        setWelcome({ url: r.data.welcome_url, emailSent: !!r.data.welcome_email_sent });
      }
      // Append to session list, keep dialog open for the next guest
      setSavedGuests((prev) => [
        ...prev,
        {
          id: r.data?.registration_id,  // needed for the inline X delete on the chip
          last_name: form.last_name.toUpperCase(),
          first_name: form.first_name.toUpperCase(),
          guest_type: form.guest_type || "16",
          doc_type: form.doc_type,
          doc_number: form.doc_number.toUpperCase(),
        },
      ]);
      toast.success(IT ? "Ospite registrato" : "Guest registered");
      // Reset for the next guest — in manual mode the host chains 3–4
      // companions from the same dialog, so we stay on the review step with a
      // blank form (only the "guest #" auto-increments to Familiare = "19").
      // In scan mode we go back to the capture step so the next photo can be
      // taken as usual.
      if (startInManual) {
        setForm({
          guest_type: "19",  // subsequent → Familiare (host can override)
          last_name: "", first_name: "", gender: "", date_of_birth: "",
          doc_type: "IDELE", doc_number: "", codice_fiscale: "", mrz: "",
          citizenship_code: "100000100",
          country_of_birth_code: "100000100",
          comune_of_birth_code: "",
          province_of_birth: "",
          birth_in_italy: true,
          issuing_country_iso3: "", expiry_date: "", notes: "",
        });
        setStep("review");
        setImageB64(null); setExtracted(null);
        setMrzParsed(null); setMrzComparison([]);
      } else if (scanMode === "nfc") {
        // Batch scan flow — return to the queue so the host can review the
        // next family member without re-opening the camera. Queue state is
        // lifted to this component so it survives the review roundtrip.
        setStep("nfc");
        setImageB64(null); setExtracted(null); setForm(null);
        setMrzParsed(null); setMrzComparison([]);
      } else {
        setStep("capture");
        setImageB64(null); setExtracted(null); setForm(null);
        setMrzParsed(null); setMrzComparison([]);
      }
    } catch (e) {
      toast.error(errMsg(e, IT ? "Salvataggio fallito" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const finishSession = () => {
    if (savedGuests.length === 0) { onClose(); return; }
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && finishSession()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {IT ? "Sessione check-in" : "Check-in session"}
          </DialogTitle>
          <DialogDescription>
            {booking && (
              <>
                <span className="font-semibold">{booking.guest_name}</span> · {booking.room_name || "—"} · {nightsOverride} {IT ? "notti" : "nights"}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Session tracker: ospiti già registrati in questa sessione */}
        {savedGuests.length > 0 && (
          <div className="border border-sage/30 bg-sage/5 rounded-md p-3 space-y-2" data-testid="scan-session-tracker">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="uppercase tracking-widest text-[10px] text-sage font-semibold">
                {IT ? `Ospiti registrati in questa sessione (${savedGuests.length})` : `Guests registered in this session (${savedGuests.length})`}
              </div>
              <Button
                variant="outline" size="sm"
                onClick={finishSession}
                className="border-sage/50 text-sage hover:bg-sage hover:text-white gap-1.5"
                data-testid="scan-session-finish"
                title={IT ? "Chiude la sessione senza salvare eventuali dati in modifica — usa \"Registra\" per salvare l'ospite corrente" : "Closes the session without saving any in-progress form — use \"Register\" to save the current guest"}
              >
                <X className="w-3.5 h-3.5"/>
                {IT ? "Annulla ed Esci" : "Cancel & Exit"}
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {savedGuests.map((g, i) => (
                <span
                  key={i}
                  data-testid={`scan-session-guest-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-sage/40 bg-white"
                >
                  <span className="text-[9px] font-mono text-sage bg-sage/10 px-1 rounded">{g.guest_type}</span>
                  <span className="font-semibold">{g.first_name} {g.last_name}</span>
                  <span className="text-muted-foreground">· {g.doc_number}</span>
                  {g.id && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(IT ? `Rimuovere ${g.first_name} ${g.last_name} da questa sessione? La registrazione verrà eliminata definitivamente.` : `Remove ${g.first_name} ${g.last_name} from this session? The registration will be permanently deleted.`)) return;
                        try {
                          await api.delete(`/guests/${g.id}`);
                          setSavedGuests((prev) => prev.filter((_, idx) => idx !== i));
                          toast.success(IT ? "Registrazione rimossa" : "Registration removed");
                        } catch (e) { toast.error(errMsg(e, IT ? "Errore" : "Error")); }
                      }}
                      data-testid={`scan-session-guest-remove-${i}`}
                      className="w-4 h-4 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center transition-colors"
                      title={IT ? "Rimuovi questa registrazione" : "Remove this registration"}
                    >
                      <X className="w-3 h-3"/>
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Treasure-Map welcome — shown as soon as the first guest is
            registered. One-tap WhatsApp send with a pre-composed Italian
            welcome that carries the deep link to the hunt for this city. */}
        {hunt?.available && hunt.hunt && (
          <div className="border border-primary/40 bg-primary/5 rounded-md p-4" data-testid="hunt-welcome-panel">
            <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
              <img
                src={hunt.hunt.hero_image}
                alt=""
                className="w-24 h-24 object-cover rounded shadow shrink-0"
                data-testid="hunt-welcome-hero"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="uppercase tracking-widest text-[10px] text-primary font-semibold flex items-center gap-1.5">
                  <Gift className="w-3 h-3"/>{IT ? "Mappa del Tesoro — regalo di benvenuto" : "Treasure Map — welcome gift"}
                </div>
                <div className="text-sm font-display italic leading-tight" data-testid="hunt-welcome-title">
                  {hunt.hunt.title}
                </div>
                {hunt.hunt.subtitle && <div className="text-xs text-muted-foreground italic">{hunt.hunt.subtitle}</div>}
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {hunt.whatsapp_url ? (
                    <a
                      href={hunt.whatsapp_url}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-full bg-[#25D366] text-white hover:bg-[#128C7E] transition-all lift"
                      data-testid="hunt-welcome-wa-btn"
                    >
                      <MessageCircle className="w-3.5 h-3.5"/>{IT ? "Invia benvenuto su WhatsApp" : "Send welcome on WhatsApp"}
                    </a>
                  ) : (
                    <span className="text-[11px] italic text-muted-foreground">
                      {IT ? "Numero WhatsApp non disponibile" : "No WhatsApp number on file"}
                    </span>
                  )}
                  <Button
                    variant="outline" size="sm"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(hunt.deep_link); toast.success(IT ? "Link copiato" : "Link copied"); }
                      catch { toast.error(IT ? "Copia non riuscita" : "Copy failed"); }
                    }}
                    className="gap-1.5"
                    data-testid="hunt-welcome-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5"/>{IT ? "Copia link" : "Copy link"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Welcome invite — QR + link the guest scans to unlock the 5% signup discount */}
        {welcome && (
          <div className="border border-primary/30 bg-primary/5 rounded-md p-4" data-testid="welcome-invite-panel">
            <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
              <div className="bg-white rounded p-2 border border-primary/30 shrink-0">
                <QRCode value={welcome.url} size={112} bgColor="#ffffff" fgColor="#231F1B" data-testid="welcome-qr"/>
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="uppercase tracking-widest text-[10px] text-primary font-semibold flex items-center gap-1.5">
                  <Gift className="w-3 h-3"/>{IT ? "Invita l'ospite a iscriversi (sconto 5%)" : "Invite the guest to sign up (5% off)"}
                </div>
                <div className="text-sm text-foreground">
                  {IT
                    ? "Fai scansionare il QR o condividi il link: l'ospite crea l'account in un tap e riceve subito il codice sconto."
                    : "Ask the guest to scan the QR or share the link: they create the account in a tap and get the discount code."}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    readOnly
                    value={welcome.url}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-[200px] text-xs font-mono bg-white border border-border rounded px-2 py-1.5"
                    data-testid="welcome-link-input"
                  />
                  <Button
                    variant="outline" size="sm"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(welcome.url); toast.success(IT ? "Link copiato" : "Link copied"); }
                      catch { toast.error(IT ? "Copia non riuscita" : "Copy failed"); }
                    }}
                    className="gap-1.5"
                    data-testid="welcome-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5"/>{IT ? "Copia" : "Copy"}
                  </Button>
                </div>
                {welcome.emailSent ? (
                  <div className="text-[11px] text-sage flex items-center gap-1.5" data-testid="welcome-email-status">
                    <Mail className="w-3 h-3"/>{IT ? "Email di invito inviata all'ospite" : "Invitation email sent to the guest"}
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="welcome-email-status">
                    <Mail className="w-3 h-3"/>{IT ? "Email non inviata (email mancante o account già esistente)" : "Email not sent (missing email or account already exists)"}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {step === "modepick" && (
          <ScanModePicker
            IT={IT}
            onPick={(m) => {
              if (m === "manual") {
                // Land the host directly on the empty review form.
                setScanMode("manual");
                setStep("review");
                const [firstFromBooking, ...restBooking] = (booking?.guest_name || "").split(" ");
                setForm({
                  guest_type: savedGuests.length === 0 ? "17" : "19",
                  last_name: (restBooking.join(" ") || "").toUpperCase(),
                  first_name: (firstFromBooking || "").toUpperCase(),
                  gender: "", date_of_birth: "",
                  doc_type: "IDELE", doc_number: "", codice_fiscale: "", mrz: "",
                  citizenship_code: "100000100",
                  country_of_birth_code: "100000100",
                  comune_of_birth_code: "",
                  province_of_birth: "",
                  birth_in_italy: true,
                  issuing_country_iso3: "", expiry_date: "", notes: "",
                });
              } else if (m === "nfc") {
                setScanMode("nfc");
                setStep("nfc");
              } else {
                setScanMode(m);
                setStep("capture");
              }
            }}
          />
        )}

        {step === "nfc" && (
          <NfcDropZone
            IT={IT}
            queue={nfcQueue}
            setQueue={setNfcQueue}
            onBack={() => { setScanMode(null); setStep("modepick"); }}
            onParsed={(x, mrzParsedResp, mrzComparisonResp) => {
              setStep("review");
              setExtracted(x);
              setConfidence(x.confidence || "high");
              setMrzParsed(mrzParsedResp || null);
              setMrzComparison(mrzComparisonResp || []);
              setForm({
                guest_type: nextGuestType(),
                last_name: x.last_name || "", first_name: x.first_name || "",
                gender: x.gender || "", date_of_birth: x.date_of_birth || "",
                doc_type: x.doc_type || "IDELE", doc_number: x.doc_number || "",
                codice_fiscale: x.codice_fiscale || "", mrz: x.mrz || "",
                citizenship_code: ISO3_TO_ISTAT[x.citizenship_iso3] || "100000100",
                country_of_birth_code: ISO3_TO_ISTAT[x.country_of_birth_iso3] || "100000100",
                comune_of_birth_code: "", province_of_birth: "",
                birth_in_italy: !x.country_of_birth_iso3 || x.country_of_birth_iso3 === "ITA",
                issuing_country_iso3: x.issuing_country_iso3 || "",
                expiry_date: x.expiry_date || "", notes: "",
              });
            }}
            onBatchRegister={async (rows, onProgress) => {
              // Auto-registration loop — fires one /reception/checkin per row
              // without opening the review form. Missing required fields are
              // reported per-row via the progress callback.
              let ok = 0, err = 0;
              for (let i = 0; i < rows.length; i++) {
                const x = rows[i].extracted;
                if (!x.last_name || !x.first_name || !x.doc_number || !x.gender || !x.date_of_birth) {
                  err++; onProgress?.({ i, ok, err, row: rows[i], skipped: true }); continue;
                }
                try {
                  const gt = savedGuests.length + ok === 0 ? "17" : "19"; // first = capofamiglia
                  const r = await api.post("/reception/checkin", {
                    booking_id: booking.id, guest_type: gt,
                    nights: Math.max(1, Math.min(30, Number(nightsOverride) || defaultNights)),
                    last_name: x.last_name, first_name: x.first_name,
                    gender: x.gender, date_of_birth: x.date_of_birth,
                    country_of_birth_code: ISO3_TO_ISTAT[x.country_of_birth_iso3] || "100000100",
                    citizenship_code: ISO3_TO_ISTAT[x.citizenship_iso3] || "100000100",
                    comune_of_birth_code: null, province_of_birth: null,
                    doc_type: x.doc_type || "IDELE", doc_number: x.doc_number,
                    doc_place_code: ISO3_TO_ISTAT[x.issuing_country_iso3] || "100000100",
                    codice_fiscale: x.codice_fiscale || null, mrz: x.mrz || null, notes: null,
                  });
                  ok++;
                  setSavedGuests((prev) => [...prev, {
                    guest_type: gt, first_name: x.first_name, last_name: x.last_name,
                    doc_number: x.doc_number, saved_at: new Date().toISOString(),
                    alloggiati_row: r.data?.alloggiati_row,
                  }]);
                  if (r.data?.welcome_url && !welcome) {
                    setWelcome({ url: r.data.welcome_url, emailSent: !!r.data.welcome_email_sent });
                  }
                } catch { err++; }
                onProgress?.({ i, ok, err, row: rows[i] });
              }
              toast.success(IT ? `${ok} ospiti registrati · ${err} scartati` : `${ok} guests registered · ${err} skipped`);
              return { ok, err };
            }}
          />
        )}

        {step === "capture" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1.5">
                <span className="uppercase tracking-widest text-[10px] text-muted-foreground">{IT ? "Modalità" : "Mode"}:</span>
                <span className="font-semibold text-primary">
                  {scanMode === "mrz" ? (IT ? "Codici/MRZ" : "Codes/MRZ") : (IT ? "OCR Testuale" : "Text OCR")}
                </span>
              </span>
              <button
                type="button"
                onClick={() => { setScanMode(null); setStep("modepick"); }}
                className="text-[11px] text-muted-foreground hover:text-primary underline"
                data-testid="scan-change-mode"
              >
                {IT ? "Cambia modalità" : "Change mode"}
              </button>
            </div>
            <CaptureStep IT={IT} onCapture={runOcr} scanMode={scanMode}/>
          </div>
        )}

        {step === "processing" && (
          <div className="py-12 text-center space-y-3">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary"/>
            <div className="text-sm text-muted-foreground">
              {IT ? "Estrazione dati dal documento…" : "Extracting data from the document…"}
            </div>
            <div className="text-xs text-muted-foreground">
              {IT ? "Gemini 3 Flash · qualche secondo" : "Gemini 3 Flash · a few seconds"}
            </div>
          </div>
        )}

        {step === "review" && form && (() => {
          const PASSPORT_TYPES = new Set(["PASOR", "PASDI", "PASSE"]);
          const isPassport = PASSPORT_TYPES.has(form.doc_type);
          const showMrz = isPassport;
          const mismatchByField = {};
          if (isPassport && mrzParsed) {
            for (const c of mrzComparison) mismatchByField[c.field] = c;
          }
          const applyMrzToField = (field) => {
            if (!mrzParsed) return;
            const mrzToForm = {
              last_name: "last_name",
              first_name: "first_name",
              doc_number: "doc_number",
              date_of_birth: "date_of_birth",
              gender: "gender",
              expiry_date: "expiry_date",
              nationality_iso3: "citizenship_iso3",
              issuing_country_iso3: "issuing_country_iso3",
            };
            const patch = {};
            for (const [mk, fk] of Object.entries(mrzToForm)) {
              if (field && fk !== field) continue;
              if (mrzParsed[mk]) patch[fk] = mrzParsed[mk];
            }
            setForm((f) => ({ ...f, ...patch }));
          };
          const invalidChecks = isPassport && mrzParsed
            ? Object.entries(mrzParsed.checks || {}).filter(([, ok]) => ok === false).map(([k]) => k)
            : [];
          const highlight = (field) => {
            const c = mismatchByField[field];
            if (!c || c.match === true) return "";
            if (c.match === false) return "border-destructive/60 ring-1 ring-destructive/40";
            return "border-amber-500/50";
          };
          return (
          <div className="space-y-4">
            {confidence && (
              <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md border ${
                confidence === "high" ? "bg-sage/10 border-sage/40 text-sage" :
                confidence === "medium" ? "bg-amber-500/10 border-amber-500/40 text-amber-700" :
                "bg-destructive/10 border-destructive/40 text-destructive"
              }`}>
                <ShieldCheck className="w-4 h-4"/>
                <span>
                  {IT ? "Confidenza OCR" : "OCR confidence"}: <b>{confidence}</b>
                  {" · "}
                  {IT ? "Verifica sempre i dati prima di salvare" : "Always verify the data before saving"}
                </span>
              </div>
            )}

            {isPassport && mrzParsed && (
              <div className="border border-border rounded-md p-3 bg-sand/40 space-y-2" data-testid="mrz-panel">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-xs">
                    <ShieldCheck className="w-4 h-4 text-primary"/>
                    <span className="uppercase tracking-widest text-[10px] font-semibold text-sage">
                      {IT ? "Verifica MRZ (banda macchina) — passaporto" : "MRZ verification — passport"}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                      invalidChecks.length === 0 ? "border-sage/40 bg-sage/10 text-sage" : "border-destructive/40 bg-destructive/10 text-destructive"
                    }`} data-testid="mrz-check-status">
                      {invalidChecks.length === 0
                        ? (IT ? "Check digit OK" : "Check digits OK")
                        : (IT ? `${invalidChecks.length} check invalido` : `${invalidChecks.length} invalid check(s)`)}
                    </span>
                  </div>
                  {mrzComparison.some((c) => c.match === true) && (
                    <button
                      type="button"
                      onClick={() => applyMrzToField(null)}
                      className="text-[11px] underline text-primary hover:no-underline"
                      data-testid="mrz-apply-all"
                    >
                      {IT ? "Usa tutti i valori MRZ" : "Use all MRZ values"}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
                  {mrzComparison.map((c) => (
                    <div
                      key={c.field}
                      data-testid={`mrz-cmp-${c.field}`}
                      className={`flex items-center justify-between gap-2 px-2 py-1 rounded ${
                        c.match === false ? "bg-destructive/10 border border-destructive/30" :
                        c.match === true  ? "bg-sage/5" :
                        "bg-muted/30"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="uppercase tracking-widest text-[9px] text-muted-foreground">{c.field}</div>
                        <div className="truncate">
                          <span className="font-mono">{String(c.mrz ?? "—")}</span>
                          {c.match === false && (
                            <>
                              {" ≠ "}
                              <span className="font-mono text-destructive">{String(c.visual ?? "—")}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {c.match === false && (
                        <button
                          type="button"
                          onClick={() => applyMrzToField(c.field)}
                          className="text-[10px] px-2 py-0.5 rounded-full border border-destructive/40 text-destructive hover:bg-destructive hover:text-white transition-colors whitespace-nowrap"
                          data-testid={`mrz-apply-${c.field}`}
                        >
                          {IT ? "Usa MRZ" : "Use MRZ"}
                        </button>
                      )}
                      {c.match === true && <Check className="w-3.5 h-3.5 text-sage flex-shrink-0"/>}
                    </div>
                  ))}
                </div>
                {invalidChecks.length > 0 && (
                  <div className="text-[11px] text-destructive">
                    {IT ? "Check digit invalidi: " : "Invalid check digits: "}
                    <span className="font-mono">{invalidChecks.join(", ")}</span>
                    {" — "}{IT ? "il documento potrebbe essere illeggibile o contraffatto." : "the document may be unreadable or forged."}
                  </div>
                )}
              </div>
            )}
            {isPassport && !mrzParsed && form.mrz && (
              <div className="text-xs px-3 py-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700" data-testid="mrz-unparseable">
                {IT ? "MRZ presente ma non interpretabile come TD3 — verifica i dati manualmente." : "MRZ present but not parseable as TD3 — verify data manually."}
              </div>
            )}

            {/* Payment status editor — visible for every reservation so the
                host can flip it (Da incassare → Pagato) right after check-in. */}
            <div className="border border-border rounded-md p-3 bg-card flex items-center gap-3 flex-wrap" data-testid="scan-payment-status-card">
              <div className="flex-1 min-w-[180px]">
                <div className="uppercase tracking-widest text-[10px] text-primary font-semibold">
                  {IT ? "Stato pagamento" : "Payment status"}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {IT ? "Imposta lo stato incasso della prenotazione — attivo anche per righe OTA." : "Set the reservation's collection status — works for OTA rows too."}
                </p>
              </div>
              <Select value={paymentStatus} onValueChange={updatePaymentStatus} disabled={savingPs}>
                <SelectTrigger className="w-48" data-testid="scan-payment-status-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{IT ? "Da incassare" : "To collect"}</SelectItem>
                  <SelectItem value="paid">{IT ? "Pagato" : "Paid"}</SelectItem>
                  <SelectItem value="refunded">{IT ? "Rimborsato" : "Refunded"}</SelectItem>
                  <SelectItem value="no_show_charged">{IT ? "Penale incassata" : "No-show charged"}</SelectItem>
                </SelectContent>
              </Select>
              {isDirect && (
                <Button
                  type="button"
                  size="sm"
                  onClick={emettiRicevuta}
                  disabled={issuing || savingPs}
                  className="bg-sage text-white hover:bg-sage/90 lift"
                  data-testid="scan-issue-receipt-btn"
                  title={IT ? "Segna come pagata (se serve) e genera al volo il documento fiscale" : "Mark paid (if needed) and issue the fiscal document"}
                >
                  <Receipt className="w-3.5 h-3.5 mr-1"/>
                  {issuing ? (IT ? "Emissione…" : "Issuing…") : (IT ? "Emetti ricevuta" : "Issue receipt")}
                </Button>
              )}
            </div>

            {/* Arrived toggle — mirror of Departed. Together they surface the
                full reception status flow directly on the guest sheet. */}
            <div className="border border-border rounded-md p-3 bg-card flex items-center gap-3 flex-wrap" data-testid="scan-arrived-card">
              <div className="flex-1 min-w-[180px]">
                <div className="uppercase tracking-widest text-[10px] text-primary font-semibold">
                  {IT ? "Arrivato" : "Arrived"}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {IT
                    ? "Segna il check-in fisico dell'ospite — abilita il conteggio schedine e i template di benvenuto."
                    : "Mark the guest's physical check-in — enables Alloggiati line count and welcome templates."}
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={arrived}
                  disabled={savingArr}
                  onChange={(e) => toggleArrived(e.target.checked)}
                  className="w-5 h-5 accent-sage cursor-pointer"
                  data-testid="scan-arrived-toggle"
                  aria-label={IT ? "Segna come arrivato" : "Mark as arrived"}
                />
                <span className={arrived ? "text-sage font-semibold" : "text-muted-foreground"}>
                  {arrived ? (IT ? "Arrivato" : "Arrived") : (IT ? "In attesa" : "Pending")}
                </span>
              </label>
            </div>

            {/* Editable arrival time — Prenotazioni + Reception + Calendar
                all read from the same manual_overrides.arrival_time field. */}
            <div className="border border-border rounded-md p-3 bg-card flex items-center gap-3 flex-wrap" data-testid="scan-arrival-time-card">
              <div className="flex-1 min-w-[180px]">
                <div className="uppercase tracking-widest text-[10px] text-primary font-semibold">
                  {IT ? "Ora di arrivo" : "Arrival time"}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {IT
                    ? "Comunicata dall'ospite o stimata dall'host — visibile in Prenotazioni e Reception. Lascia vuoto per rimuovere."
                    : "Guest-provided or host-estimated ETA — surfaces on Prenotazioni + Reception. Blank clears it."}
                </p>
              </div>
              <input
                type="time"
                value={arrivalTime}
                onChange={(e) => setArrivalTime(e.target.value)}
                onBlur={(e) => e.target.value !== (booking?.arrival_time || "") && saveArrivalTime(e.target.value)}
                disabled={savingAt}
                className="h-9 px-2 rounded-md border border-border bg-background font-mono text-sm w-32"
                data-testid="scan-arrival-time-input"
                aria-label={IT ? "Ora di arrivo (HH:MM)" : "Arrival time (HH:MM)"}
              />
            </div>

            {/* Departed toggle — mirrors the reception table Partito column so
                the host can flip departure directly from the guest sheet. */}
            <div className="border border-border rounded-md p-3 bg-card flex items-center gap-3 flex-wrap" data-testid="scan-departed-card">
              <div className="flex-1 min-w-[180px]">
                <div className="uppercase tracking-widest text-[10px] text-primary font-semibold">
                  {IT ? "Partito" : "Departed"}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {IT
                    ? "Segna la partenza dell'ospite direttamente qui — libera la camera per la squadra pulizie."
                    : "Mark the guest as departed directly here — frees the room for housekeeping."}
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={departed}
                  disabled={savingDep}
                  onChange={(e) => toggleDeparted(e.target.checked)}
                  className="w-5 h-5 accent-sage cursor-pointer"
                  data-testid="scan-departed-toggle"
                  aria-label={IT ? "Segna come partito" : "Mark as departed"}
                />
                <span className={departed ? "text-sage font-semibold" : "text-muted-foreground"}>
                  {departed ? (IT ? "Partito" : "Departed") : (IT ? "In casa" : "In-house")}
                </span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>{IT ? "Permanenza (notti)" : "Stay length (nights)"}*</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={nightsOverride}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setNightsOverride(Number.isFinite(v) && v >= 1 ? Math.min(30, v) : "");
                    }}
                    onBlur={() => {
                      if (!Number.isFinite(Number(nightsOverride)) || Number(nightsOverride) < 1) {
                        setNightsOverride(defaultNights);
                      }
                    }}
                    className="w-24 font-mono"
                    data-testid="scan-form-nights"
                  />
                  <span className="text-xs text-muted-foreground">
                    {IT
                      ? `Auto-calcolato dalle date (${booking?.check_in?.slice(0,10) || "—"} → ${booking?.check_out?.slice(0,10) || "—"}, ${defaultNights} ${defaultNights === 1 ? "notte" : "notti"}). Modifica se necessario — verrà usato per Alloggiati Web.`
                      : `Auto-calculated from booking dates (${booking?.check_in?.slice(0,10) || "—"} → ${booking?.check_out?.slice(0,10) || "—"}, ${defaultNights} ${defaultNights === 1 ? "night" : "nights"}). Edit if needed — used for Alloggiati Web submission.`}
                  </span>
                </div>
              </div>
              <div className="col-span-2">
                <Label>{IT ? "Tipo alloggiato (Alloggiati Web)" : "Guest type (Alloggiati Web)"}*</Label>
                <Select value={form.guest_type} onValueChange={(v) => setForm({ ...form, guest_type: v })}>
                  <SelectTrigger data-testid="scan-form-guest-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="16">16 · {IT ? "Ospite singolo" : "Single guest"}</SelectItem>
                    <SelectItem value="17">17 · {IT ? "Capofamiglia" : "Head of family"}</SelectItem>
                    <SelectItem value="18">18 · {IT ? "Capogruppo" : "Head of group"}</SelectItem>
                    <SelectItem value="19">19 · {IT ? "Familiare" : "Family member"}</SelectItem>
                    <SelectItem value="20">20 · {IT ? "Membro gruppo" : "Group member"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{IT ? "Cognome" : "Last name"}*</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className={highlight("last_name")} data-testid="scan-form-last-name"/>
              </div>
              <div>
                <Label>{IT ? "Nome" : "First name"}*</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className={highlight("first_name")} data-testid="scan-form-first-name"/>
              </div>
              <div>
                <Label>{IT ? "Sesso" : "Gender"}*</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className={highlight("gender")} data-testid="scan-form-gender"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">M</SelectItem>
                    <SelectItem value="F">F</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{IT ? "Data di nascita" : "Date of birth"}*</Label>
                <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                  className={highlight("date_of_birth")} data-testid="scan-form-dob"/>
              </div>
              <div>
                <Label>{IT ? "Tipo documento" : "Document type"}*</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger data-testid="scan-form-doc-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((d) => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{IT ? "Numero documento" : "Document number"}*</Label>
                <Input value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value.toUpperCase() })}
                  className={highlight("doc_number")} data-testid="scan-form-doc-number"/>
              </div>
              <div>
                <Label>{IT ? "Cittadinanza" : "Citizenship"}</Label>
                <GeoSearchSelect
                  value={form.citizenship_code}
                  onChange={(code) => setForm({ ...form, citizenship_code: code })}
                  fetchUrl="/geo/stati"
                  displayKey="name_it"
                  extraKey="name_en"
                  placeholder={IT ? "Cerca Stato…" : "Search state…"}
                  testId="scan-form-citizenship"
                  IT={IT}
                  className={highlight("citizenship_iso3")}
                />
              </div>
              <div>
                <Label>{IT ? "Luogo di nascita" : "Place of birth"}</Label>
                <div className="flex gap-1.5 mb-1.5" role="tablist">
                  <Button
                    type="button" size="sm"
                    variant={form.birth_in_italy ? "default" : "outline"}
                    onClick={() => setForm({ ...form, birth_in_italy: true, country_of_birth_code: "100000100" })}
                    className="h-7 text-xs"
                    data-testid="scan-form-birth-italy"
                  >
                    {IT ? "In Italia" : "In Italy"}
                  </Button>
                  <Button
                    type="button" size="sm"
                    variant={!form.birth_in_italy ? "default" : "outline"}
                    onClick={() => setForm({ ...form, birth_in_italy: false, comune_of_birth_code: "", province_of_birth: "" })}
                    className="h-7 text-xs"
                    data-testid="scan-form-birth-abroad"
                  >
                    {IT ? "All'estero" : "Abroad"}
                  </Button>
                </div>
                {form.birth_in_italy ? (
                  <GeoSearchSelect
                    value={form.comune_of_birth_code}
                    onChange={(code, rec) => setForm({
                      ...form,
                      comune_of_birth_code: code,
                      province_of_birth: (rec && rec.prov) || "",
                      country_of_birth_code: "100000100",
                    })}
                    fetchUrl="/geo/comuni"
                    displayKey="name"
                    extraKey="prov"
                    placeholder={IT ? "Cerca Comune ISTAT…" : "Search Italian comune…"}
                    testId="scan-form-birth-comune"
                    IT={IT}
                  />
                ) : (
                  <GeoSearchSelect
                    value={form.country_of_birth_code}
                    onChange={(code) => setForm({
                      ...form,
                      country_of_birth_code: code,
                      comune_of_birth_code: "",
                      province_of_birth: "",
                    })}
                    fetchUrl="/geo/stati"
                    displayKey="name_it"
                    extraKey="name_en"
                    placeholder={IT ? "Cerca Stato…" : "Search state…"}
                    testId="scan-form-birth-country"
                    IT={IT}
                  />
                )}
              </div>
              <div className="col-span-2">
                <Label>{IT ? "Codice Fiscale" : "Italian tax code (CF)"}</Label>
                <Input value={form.codice_fiscale} maxLength={16}
                  onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })}
                  className="font-mono uppercase" data-testid="scan-form-cf"/>
              </div>
              {showMrz && form.mrz && (
                <div className="col-span-2">
                  <Label>MRZ</Label>
                  <Textarea rows={2} value={form.mrz} onChange={(e) => setForm({ ...form, mrz: e.target.value })}
                    className="font-mono text-xs" data-testid="scan-form-mrz"/>
                </div>
              )}
              <div className="col-span-2">
                <Label>{IT ? "Note (facoltative)" : "Notes (optional)"}</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="scan-form-notes"/>
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => setStep("capture")} data-testid="scan-form-back">
                <RefreshCw className="w-3.5 h-3.5 mr-1"/> {IT ? "Rifai la scansione" : "Rescan"}
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90 lift"
                data-testid="scan-form-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Check className="w-4 h-4 mr-1"/>}
                {savedGuests.length === 0
                  ? (IT ? "Registra e aggiungi altro ospite" : "Register & add another")
                  : (IT ? `Registra ospite #${savedGuests.length + 1}` : `Register guest #${savedGuests.length + 1}`)}
              </Button>
            </div>
          </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
